import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, TrendingUp, TrendingDown, DollarSign, ShoppingBag, Star, Clock } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
export type ItemAnalytics = {
  id: number;
  name: string;
  ordersThisWeek: number;
  ordersLastWeek: number;
  trendPct: number;
  popularity: number;
  revenueThisWeek: number;
  weeklyData: {
    day: string;
    orders: number;
  }[];
  hourlyData: {
    hour: string;
    orders: number;
  }[];
  rating: number;
  reviewCount: number;
};
export function ItemAnalyticsModal({
  item,
  onClose
}: {
  item: ItemAnalytics | null;
  onClose: () => void;
}) {
  return <AnimatePresence data-id="element-1923">
      {item && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm" data-id="element-1924" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-1925">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto pointer-events-auto" data-id="element-1926">
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10" data-id="element-1927">
                <div data-id="element-1928">
                  <div className="text-xs text-gray-500 font-medium mb-0.5" data-id="element-1929">
                    Item Analytics
                  </div>
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-1930">
                    {item.name}
                  </h2>
                </div>
                <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors" data-id="element-1931">
                  <X size={20} data-id="element-1932" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6" data-id="element-1933">
                {/* Stat Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-1934">
                  <StatCard icon={<ShoppingBag size={16} data-id="element-1936" />} label="Orders this week" value={item.ordersThisWeek.toLocaleString()} trend={item.trendPct} color="orange" data-id="element-1935" />
                  <StatCard icon={<DollarSign size={16} data-id="element-1938" />} label="Revenue" value={`$${item.revenueThisWeek.toLocaleString()}`} trend={item.trendPct} color="green" data-id="element-1937" />
                  <StatCard icon={<Star size={16} data-id="element-1940" />} label="Rating" value={item.rating.toFixed(1)} sub={`${item.reviewCount} reviews`} color="yellow" data-id="element-1939" />
                  <StatCard icon={<TrendingUp size={16} data-id="element-1942" />} label="Popularity" value={`${item.popularity}/100`} sub={item.popularity >= 75 ? 'Top seller' : item.popularity >= 50 ? 'Above average' : 'Building up'} color="blue" data-id="element-1941" />
                </div>

                {/* Daily Orders */}
                <div className="bg-gray-50/60 border border-gray-100 rounded-xl p-5" data-id="element-1943">
                  <div className="flex items-center justify-between mb-4" data-id="element-1944">
                    <div data-id="element-1945">
                      <h3 className="font-semibold text-gray-900 text-sm" data-id="element-1946">
                        Daily orders
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-1947">
                        Last 7 days
                      </p>
                    </div>
                    <div className={`flex items-center gap-1 text-xs font-semibold ${item.trendPct >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-id="element-1948">
                      {item.trendPct >= 0 ? <TrendingUp size={14} data-id="element-1949" /> : <TrendingDown size={14} data-id="element-1950" />}
                      {item.trendPct >= 0 ? '+' : ''}
                      {item.trendPct.toFixed(1)}% vs last week
                    </div>
                  </div>
                  <div className="h-48 -mx-2" data-id="element-1951">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-1952">
                      <AreaChart data={item.weeklyData} margin={{
                    top: 5,
                    right: 10,
                    left: 0,
                    bottom: 0
                  }} data-id="element-1953">
                        <defs data-id="element-1954">
                          <linearGradient id="orderGradient" x1="0" y1="0" x2="0" y2="1" data-id="element-1955">
                            <stop offset="0%" stopColor="#f97316" stopOpacity={0.3} data-id="element-1956" />
                            <stop offset="100%" stopColor="#f97316" stopOpacity={0} data-id="element-1957" />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} data-id="element-1958" />
                        <XAxis dataKey="day" stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} data-id="element-1959" />
                        <YAxis stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} data-id="element-1960" />
                        <Tooltip contentStyle={{
                      background: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 12
                    }} data-id="element-1961" />
                        <Area type="monotone" dataKey="orders" stroke="#f97316" strokeWidth={2.5} fill="url(#orderGradient)" data-id="element-1962" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Peak Hours */}
                <div className="bg-gray-50/60 border border-gray-100 rounded-xl p-5" data-id="element-1963">
                  <div className="flex items-center gap-2 mb-4" data-id="element-1964">
                    <Clock size={16} className="text-gray-500" data-id="element-1965" />
                    <h3 className="font-semibold text-gray-900 text-sm" data-id="element-1966">
                      Peak hours
                    </h3>
                  </div>
                  <div className="h-40 -mx-2" data-id="element-1967">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-1968">
                      <BarChart data={item.hourlyData} margin={{
                    top: 5,
                    right: 10,
                    left: 0,
                    bottom: 0
                  }} data-id="element-1969">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} data-id="element-1970" />
                        <XAxis dataKey="hour" stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} data-id="element-1971" />
                        <YAxis stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} data-id="element-1972" />
                        <Tooltip contentStyle={{
                      background: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 12
                    }} data-id="element-1973" />
                        <Bar dataKey="orders" fill="#fb923c" radius={[4, 4, 0, 0]} data-id="element-1974" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
function StatCard({
  icon,
  label,
  value,
  trend,
  sub,
  color
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  trend?: number;
  sub?: string;
  color: 'orange' | 'green' | 'yellow' | 'blue';
}) {
  const colorMap = {
    orange: 'bg-orange-50 text-orange-600',
    green: 'bg-emerald-50 text-emerald-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    blue: 'bg-blue-50 text-blue-600'
  };
  return <div className="bg-white border border-gray-100 rounded-xl p-4" data-id="element-1975">
      <div className="flex items-center gap-2 mb-2" data-id="element-1976">
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${colorMap[color]}`} data-id="element-1977">
          {icon}
        </div>
        <span className="text-[11px] font-medium text-gray-500" data-id="element-1978">{label}</span>
      </div>
      <div className="text-xl font-bold text-gray-900" data-id="element-1979">{value}</div>
      {trend !== undefined && <div className={`text-[11px] font-semibold mt-0.5 flex items-center gap-0.5 ${trend >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-id="element-1980">
          {trend >= 0 ? <TrendingUp size={11} data-id="element-1981" /> : <TrendingDown size={11} data-id="element-1982" />}
          {trend >= 0 ? '+' : ''}
          {trend.toFixed(1)}%
        </div>}
      {sub && <div className="text-[11px] text-gray-500 mt-0.5" data-id="element-1983">{sub}</div>}
    </div>;
}